import Link from 'next/link';
import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  AvailableService,
  ClientRegistrationFields,
  CommissioningBodyOption,
  RegistrationServiceFields,
} from 'types/registration';
import { i18n as I18n, useTranslation } from 'utils/i18n';
import * as yup from 'yup';
// Constants
import { getYupServiceSchema } from '../../../constants/yupServiceSchema';
import { useApiRequest } from '../../../hooks/useApiRequest';
// Hooks
import useValidation from '../../../hooks/useValidation';
import type { FieldChangeEvent } from '../../../types';
import Checkbox from '../../fields/checkbox';
// Components
import Service from '../service';
import ServiceDetailsContainer from '../serviceDetailsContainer';

// eslint-disable-next-line func-names
yup.addMethod(yup.object, 'uniqueProperty', function (propertyName, message) {
  // eslint-disable-next-line func-names
  return this.test('unique', message, function (value) {
    if (!value?.[propertyName]) {
      return true;
    }

    const { path } = this;
    const options = [...this.parent];
    const currentIndex = options.indexOf(value);
    const subOptions = options.slice(0, currentIndex);

    if (
      subOptions.some(option => option[propertyName] === value[propertyName])
    ) {
      throw this.createError({
        path: `${path}.${propertyName}`,
        message,
      });
    }

    return true;
  });
});

// A function rather than a constant: both the message below and the labels
// inside the service schema are translations, which have to resolve after
// i18next has been initialised.
const getServicesSchema = () =>
  yup
    .array()
    .of(
      getYupServiceSchema().uniqueProperty(
        'title',
        I18n.t('registration.service_details.services_not_unique')
      )
    );

interface Props {
  fields: ClientRegistrationFields;
  submissionErrors?: string[];
  handleFieldChange: (event: FieldChangeEvent<unknown>) => void;
  handleResetField: (field: string, value: unknown) => void;
  handleSubmitCallback: () => void;
  handleGoBack?: () => void;
  termsAndConditionsEnabled?: boolean;
  previousStep?: number;
}

const NewServiceDetails = ({
  fields,
  submissionErrors,
  handleFieldChange,
  handleResetField,
  handleSubmitCallback,
  handleGoBack,
  termsAndConditionsEnabled = true,
  previousStep = 2,
}: Props) => {
  const { t } = useTranslation('common');
  const schema = useMemo(() => {
    const additionalSchemas: Record<string, yup.Schema> = {};

    if (termsAndConditionsEnabled) {
      additionalSchemas.agreePrivacy = yup.string().required(
        t('validations.required', {
          name: t('registration.service_details.fields.agree_privacy.label'),
        })
      );

      additionalSchemas.agreeTerms = yup.string().required(
        t('validations.required', {
          name: t('registration.service_details.fields.agree_terms.label'),
        })
      );
    }

    return yup.object().shape({
      services: getServicesSchema(),
      ...additionalSchemas,
    });
  }, [termsAndConditionsEnabled, t]);

  const { errors, validate, isValid } = useValidation(fields, schema);

  const [availableServices, setAvailableServices] = useState<
    AvailableService[]
  >([]);
  const [commissioningBodies, setCommissioningBodies] = useState<
    CommissioningBodyOption[]
  >([]);

  const apiRequest = useApiRequest();

  useEffect(() => {
    const fetchAvailableServices = async () => {
      const { data } = await apiRequest.get<AvailableService[]>(
        '/v1/organisations/services'
      );
      setAvailableServices(data);
    };

    const fetchCommissioningBodies = async () => {
      const { data } = await apiRequest.get<
        Array<{ id: number; name: string }>
      >(`/v1/organisations/ccg_regions`);
      setCommissioningBodies(
        data.map(option => ({
          label: option.name,
          value: option.id,
        }))
      );
    };

    fetchAvailableServices();
    fetchCommissioningBodies();
  }, [apiRequest]);

  // When services change, run validation to check uniqueness of service combinations.
  const hasMounted = useRef(false);
  useEffect(() => {
    if (hasMounted.current) {
      validate();
    } else {
      hasMounted.current = true;
    }
  }, [validate]);

  const newService = () => {
    const template: Record<string, unknown> = {};
    Object.keys(fields.services[0]).forEach(key => {
      template[key] = '';
    });

    return template;
  };

  const nextServicePosition = () => {
    const currentMax = fields.services.reduce(
      (max, service) => Math.max(max, service.position),
      0
    );
    return currentMax + 1;
  };

  const addService = () => {
    const service = newService();
    service.position = nextServicePosition();
    service.isOpen = true;

    const currentServices = fields.services.map(item => ({
      ...item,
      isOpen: false,
    }));
    handleResetField('services', [...currentServices, { ...service }]);
  };

  const removeService = (position: number) => {
    const services = fields.services.filter(item => item.position !== position);
    handleResetField('services', services);
  };

  const updateService = useCallback(
    (service: RegistrationServiceFields) =>
      handleResetField('services', (services: RegistrationServiceFields[]) =>
        services.map(item =>
          item.position === service.position ? service : item
        )
      ),
    [handleResetField]
  );

  const toggleServiceVisibility = (position: number) => {
    const services = fields.services.map(item =>
      item.position === position ? { ...item, isOpen: !item.isOpen } : item
    );
    handleResetField('services', services);
  };

  const handleChange = (event: FieldChangeEvent<unknown>) => {
    handleFieldChange(event);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    validate();
    if (isValid()) {
      handleSubmitCallback();
    }

    return false;
  };

  const agreementLabelContent = (text: string, href: string) => (
    <>
      {t('registration.service_details.i_have_read_and_agree_to')}
      &nbsp;
      <Link href={href} target="_blank">
        {text}
      </Link>
    </>
  );

  return (
    <ServiceDetailsContainer
      handleSubmit={handleSubmit}
      submissionErrors={submissionErrors}
      isValid={isValid()}
      handleGoBack={handleGoBack}
      previousStep={previousStep}
    >
      <ul className="services-list" data-cy="newServicesList">
        {fields.services.map((serviceFields, index) => (
          <Service
            number={index + 1}
            key={serviceFields.position}
            isOpen={serviceFields.isOpen}
            serviceFields={serviceFields}
            availableServices={availableServices}
            commissioningBodies={commissioningBodies}
            handleUpdateService={updateService}
            handleRemoveService={
              index === 0 && fields.services.length === 1
                ? null
                : () => removeService(serviceFields.position)
            }
            handleToggleServiceVisibility={() =>
              toggleServiceVisibility(serviceFields.position)
            }
            notUnique={!!errors[`services[${index}].title`]}
          />
        ))}
      </ul>
      <p>
        <button
          onClick={addService}
          className="button button--medium button--primary"
          disabled={!getServicesSchema().isValidSync(fields.services)}
          data-testid="addService"
          data-cy="addService"
        >
          {t('registration.service_details.add_another_service')}
        </button>
      </p>
      {termsAndConditionsEnabled && (
        <div className="acceptance-container">
          <Checkbox
            label={agreementLabelContent(
              t('registration.service_details.fields.agree_privacy.label'),
              '/privacy-policy'
            )}
            name="agreePrivacy"
            id="agreePrivacy"
            checked={fields.agreePrivacy === '1'}
            value="1"
            onChange={handleChange}
          />
          <Checkbox
            label={agreementLabelContent(
              t('registration.service_details.fields.agree_terms.label'),
              '/terms-conditions'
            )}
            name="agreeTerms"
            id="agreeTerms"
            checked={fields.agreeTerms === '1'}
            value="1"
            onChange={handleChange}
          />
        </div>
      )}
    </ServiceDetailsContainer>
  );
};

export default NewServiceDetails;
